import React, { useState } from "react";

import AddIcon from "@mui/icons-material/AddCircle";
import { Button, Drawer, IconButton, Typography } from "@mui/material";
import { AsyncContent, SimpleHead } from "components";
import { IApiServiceError, useDealActions, useDealHierarchyOpenSelect, useDealSelector } from "store";
import { DealBase } from "store/types";
import { v4 as uuid } from "uuid";

import { useActionHook, useModals } from "../../../hooks";
import { SelectDeal } from "./SelectDeal";

import "./DealHierarchyPanel.styles.scss";

const DealHierarchyPanelContent: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const deal = useDealSelector();
  const { updateDealHierarchyAction, getDealAndStoreAction } = useDealActions();

  const { successModal, errorModal } = useModals();

  const { action: update } = useActionHook(updateDealHierarchyAction, {
    onSuccess: () => {
      onClose();
      successModal({ msg: "Deal hierarchy updated" });
      getDealAndStoreAction(deal.id);
    },
    onReject: (e: IApiServiceError) => {
      let msg: string = "Deal hierarchy failed";

      const { errors, message } = e;

      const errorsValues = (errors && Object.values(errors)) || [];
      if (errorsValues.length) {
        msg = errorsValues.flat().join("\n");
      } else {
        msg = message;
      }

      errorModal({ msg });
    }
  });

  const [parent, setParent] = useState<DealBase | null>(deal.parent);
  const [subAssets, setSubAssets] = useState<(DealBase | null)[]>(deal.sub_assets);
  const [parentMode, setParentMode] = useState<boolean>(!!deal.parent);
  const [subAssetKeys, setSubAssetKeys] = useState<string[]>(deal.sub_assets.map(() => uuid()));

  const onUpdateClick = () => {
    update({
      dealId: deal.id,
      parent: parent?.id || null,
      subAssets: subAssets.filter(Boolean).map((deal) => deal!.id)
    });
  };

  const removeParent = () => {
    setParent(null);
    setParentMode(false);
  };

  const selectSubAsset = (index: number, deal: DealBase | null) => {
    const copy = [...subAssets];
    copy[index] = deal;
    setSubAssets(copy);
  };

  const removeSubAsset = (index: number) => {
    setSubAssets([...subAssets.slice(0, index), ...subAssets.slice(index + 1, subAssets.length)]);
    setSubAssetKeys([...subAssetKeys.slice(0, index), ...subAssetKeys.slice(index + 1, subAssets.length)]);
  };

  const addSubAsset = () => {
    setSubAssets([...subAssets, null]);
    setSubAssetKeys([...subAssetKeys, uuid()]);
  };

  return (
    <div className="deal-hierarchy-panel">
      <SimpleHead onIconClick={onClose}>Asset hierarchy - {deal.title}</SimpleHead>
      <AsyncContent loading={false} spinnerProps={{ size: 120 }} className="body flex-col-fs-fc">
        {parentMode && <SelectDeal deal={parent} onRemove={removeParent} onSelect={setParent} title="Parent deal" />}
        {!parentMode && (
          <>
            <div className="sub-deals-title">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Sub deals
              </Typography>
              <IconButton onClick={addSubAsset}>
                <AddIcon color="primary" />
              </IconButton>
            </div>
            {subAssets.map((deal, index) => (
              <SelectDeal
                key={subAssetKeys[index]}
                onRemove={() => removeSubAsset(index)}
                deal={deal}
                onSelect={(deal) => selectSubAsset(index, deal)}
                title="Deal"
              />
            ))}
          </>
        )}
      </AsyncContent>
      <div className="footer">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={false} onClick={onUpdateClick} type="submit" variant="contained">
          Save
        </Button>
      </div>
    </div>
  );
};

export const DealHierarchyPanel: React.FC = () => {
  const open = useDealHierarchyOpenSelect();
  const { setHierarchyPanelOpenAction } = useDealActions();

  const onClose = () => setHierarchyPanelOpenAction(false);

  return (
    <Drawer
      sx={{
        ".MuiPaper-root": {
          borderTopRightRadius: "25px",
          borderBottomRightRadius: "25px"
        }
      }}
      open={open}
      onClose={onClose}
    >
      <DealHierarchyPanelContent onClose={onClose} />
    </Drawer>
  );
};
